import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    IRRIGATION_STATE_COLORS,
    IrrigationStatusIcon,
    irrigationStateTooltipTranslationKeys,
    makeDateTimeString,
    pumpStateTooltipTranslationKeys,
} from "@/features/device-status-icons/irrigation-status-icon";
import { NAMESPACES } from "@/i18n/constants";
import { IRRIGATION_STATE } from "../../../../../services/device-attributes/attributes/irrigation/definitions";
import { composeDeviceAttributes } from "../../../../../services/device-attributes/device-attributes";
import type DeviceMapEntity from "../../../../../services/map-management/map-entities/device-map-entity";

interface ValveStatusProps {
    model: DeviceMapEntity["model"];
}
/**
 * Thin extension of the IrrigationStatusIcon component that displays the icon's tooltip text next to the icon
 */
export function ValveStatus({ model }: ValveStatusProps) {
    const { t } = useTranslation(NAMESPACES.ICONS, { keyPrefix: "irrigation-status" });

    const { irrigation } = composeDeviceAttributes({ model });
    const { textColor: color } = IRRIGATION_STATE_COLORS[irrigation.irrigationState];
    const tooltipKeys =
        model?.connectedEquipment === "pump" ? pumpStateTooltipTranslationKeys : irrigationStateTooltipTranslationKeys;

    const irrigationStateTooltipParams = {
        [IRRIGATION_STATE.OPEN]: {},
        [IRRIGATION_STATE.CLOSED]: {},
        [IRRIGATION_STATE.OPENING]: {},
        [IRRIGATION_STATE.CLOSING]: {},
        [IRRIGATION_STATE.SCHEDULED_IRRIGATION]: {
            percentageScheduledIrrigationCompleted: irrigation.percentageScheduledIrrigationCompleted ?? 0,
            irrigationEndTime: makeDateTimeString(irrigation.irrigationEndTime ?? new Date()),
        },
    };

    const tooltip = t(
        tooltipKeys[irrigation.irrigationState],
        irrigationStateTooltipParams[irrigation.irrigationState],
    );
    return (
        <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{
                gap: 2,
                borderRadius: "24px",
            }}
        >
            <IrrigationStatusIcon
                irrigationState={irrigation.irrigationState}
                connectedEquipment={model?.connectedEquipment}
                includeLabel={false}
                includeTooltip={false}
            />
            <Typography
                sx={{
                    color: color,
                    fontWeight: 500,
                    fontSize: "12px",
                    mt: "2px", // vertically center the text with the icon
                }}
            >
                {tooltip}
            </Typography>
        </Stack>
    );
}
